
import { useMsal } from '@azure/msal-react';
import {
    Button,
    makeStyles,
    Spinner,
    tokens
} from '@fluentui/react-components';
import { CheckmarkCircle24Regular, Open24Regular, People24Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    teamsCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingHorizontalL,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    teamsHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    teamsIcon: {
        width: '60px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    teamsLogo: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
    teamsTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    description: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        lineHeight: '1.5',
    },
    successMessage: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: tokens.spacingHorizontalM,
        backgroundColor: '#EBF5F0',
        border: `1px solid #10B981`,
        borderRadius: tokens.borderRadiusMedium,
        color: '#047857',
    },
    successIcon: {
        fontSize: '20px',
        flexShrink: 0,
    },
    linkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        marginTop: tokens.spacingVerticalM,
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginTop: tokens.spacingVerticalM,
    },
    errorMessage: {
        padding: tokens.spacingHorizontalM,
        backgroundColor: '#FEE2E2',
        border: `1px solid #EF4444`,
        borderRadius: tokens.borderRadiusMedium,
        color: '#DC2626',
        fontSize: tokens.fontSizeBase300,
    },
});

const Teams: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const { project, refreshProject } = useProject();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const handleCreateTeams = async () => {
        if (!dataService || !project?.projektID) {
            setError('Project information not available');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            setSuccessMessage(null);

            await dataService.createTeamsForProjekt(project.projektID);
            setSuccessMessage('Teams channel created successfully!');

            // Refresh the project data
            if (refreshProject) {
                await refreshProject();
            }
        } catch (err) {
            console.error('Error creating Teams:', err);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Error creating Teams channel. Please try again.',
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenTeams = () => {
        if (project?.teamsUrl) {
            window.open(project.teamsUrl, '_blank');
        }
    };

    if (!project) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.teamsCard}>
                {/* Header */}
                <div className={styles.teamsHeader}>
                    <div className={styles.teamsIcon}>
                        <img src="/teamsLogo.png" alt="Microsoft Teams" className={styles.teamsLogo} />
                    </div>
                    <div>
                        <div className={styles.teamsTitle}>Microsoft Teams</div>
                        <div className={styles.description}>
                            Collaborate with your team members in Microsoft Teams
                        </div>
                    </div>
                </div>

                {/* Description */}
                <div className={styles.description}>
                    {project.teamsId
                        ? 'Click the button below to open your project Teams channel and start collaborating with your team.'
                        : 'Create a dedicated Microsoft Teams channel for this project to facilitate collaboration and communication among team members.'}
                </div>

                {/* Error Message */}
                {error && <div className={styles.errorMessage}>{error}</div>}

                {/* Success Message */}
                {successMessage && (
                    <div className={styles.successMessage}>
                        <CheckmarkCircle24Regular className={styles.successIcon} />
                        <span>{successMessage}</span>
                    </div>
                )}

                {/* Content based on teamsId and teamsUrl status */}
                {project.teamsId && project.teamsUrl ? (
                    <>
                        <div className={styles.successMessage}>
                            <CheckmarkCircle24Regular className={styles.successIcon} />
                            <span>Teams channel is set up and ready to use</span>
                        </div>
                        <div className={styles.linkContainer}>
                            <Button
                                icon={<Open24Regular />}
                                onClick={handleOpenTeams}
                                appearance="primary"
                            >
                                Open Teams Channel
                            </Button>
                        </div>
                    </>
                ) : project.teamsId && !project.teamsUrl ? (
                    <div className={styles.successMessage}>
                        <Spinner size="tiny" />
                        <span>Teams channel is being set up. Please wait...</span>
                    </div>
                ) : (
                    <div className={styles.buttonGroup}>
                        <Button
                            onClick={handleCreateTeams}
                            appearance="primary"
                            disabled={isLoading}
                            icon={isLoading ? undefined : <People24Regular />}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner size="tiny" />
                                    Creating Teams Channel...
                                </>
                            ) : (
                                'Create Teams Channel'
                            )}
                        </Button>
                    </div>
                )}

                {/* Loading State */}
                {isLoading && (
                    <div className={styles.loadingContainer}>
                        <Spinner label="Creating Teams channel..." />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Teams;
